import type { HelpEntry } from "./help.ts";

export const CATEGORIES_ES = [
  "Abrir y crear proyectos",
  "El mapa",
  "Editar el proyecto",
  "Modelos",
  "Calcular",
  "Resultados",
  "Otros",
] as const;

export const HELP_ES: HelpEntry[] = [
  // --- Abrir y crear proyectos ---
  {
    id: "abrir-proyecto",
    category: "Abrir y crear proyectos",
    title: "Abrir un proyecto existente",
    keywords: ["carpeta", "ehd", "chrome", "edge", "showdirectorypicker"],
    content: [
      'Botón "Abrir" en la pantalla inicial: elige la carpeta que contiene el fichero .ehd y, junto a él, la carpeta con las series de entrada (precipitación, ETP...). Hace falta un navegador con soporte de la File System Access API — Chrome o Edge, no Firefox ni Safari.',
      "Si la carpeta no tiene ningún .ehd pero sí los ficheros CSV (cuencas, zonas, parámetros...), se detecta y se abre igual a partir de ellos, sin tener que crear un .ehd a mano.",
    ],
  },
  {
    id: "formatos-proyecto",
    category: "Abrir y crear proyectos",
    title: "Modelos antiguos y nuevos (con zonas)",
    keywords: ["sin zonas", "antiguo", "viejo", "compatibilidad"],
    content: [
      "Se abren igual, sin que tengas que indicar nada: los modelos más antiguos (de antes de que existiera el concepto de Zona) y los actuales (con Zonas) se detectan solos al leer el .ehd.",
      "En un modelo antiguo, cada subcuenca se convierte automáticamente en su propia zona al abrirlo — así el resto de la app (edición, cálculo...) funciona exactamente igual en los dos casos.",
    ],
  },
  {
    id: "formatos-series",
    category: "Abrir y crear proyectos",
    title: "Formatos de precipitación, ETP, temperatura y bombeos",
    keywords: ["csv", "inp", "series", "entrada", "ficheros", "formato de fichero"],
    content: [
      'También hay dos formatos, según el modelo, y también se detectan solos por el contenido del fichero (no hace falta indicar cuál es): un CSV separado por ";" con la fecha en dd/mm/aaaa (modelos con Zonas), o un formato más antiguo con bloques de etiquetas y columnas separadas por tabulador (modelos antiguos, ficheros .inp).',
      "En el formato antiguo las columnas se enlazan con cada subcuenca por un número de columna guardado en el proyecto, no por el nombre — al revés que en el formato nuevo.",
    ],
  },
  {
    id: "crear-proyecto",
    category: "Abrir y crear proyectos",
    title: "Crear un proyecto nuevo desde cero",
    keywords: ["asistente", "wizard", "nuevo", "shapefile", "paso a paso"],
    content: [
      'Botón "Crear nuevo" en la pantalla inicial. El asistente te lleva paso a paso: subcuencas (importadas de un shapefile, o creadas a mano si no tienes uno), zonas (cada una con un modelo y sus parámetros), puntos de desagüe y acuíferos, la topología (qué subcuenca vierte en qué punto/acuífero), y por último las series de precipitación/ETP.',
      'Si prefieres no seguir el asistente entero, puedes crear un proyecto mínimo y añadir el resto después desde "Editar proyecto".',
    ],
  },
  {
    id: "ejemplos",
    category: "Abrir y crear proyectos",
    title: "Probar con un ejemplo",
    keywords: ["turia", "demo", "prueba", "con gis", "sin gis"],
    content: [
      'Botones "Con GIS"/"Sin GIS" en la pantalla inicial: cargan un proyecto real de ejemplo (77 subcuencas del Turia) sin que tengas que subir nada tuyo, para explorar la app antes de usar tus propios datos — uno con el mapa geográfico, el otro con el lienzo de tarjetas.',
      'Los cambios sobre un ejemplo no se guardan solos (verás un aviso fijo abajo mientras tanto) — usa "Guardar copia como…" en cualquier momento para convertirlo en un proyecto tuyo de verdad, con autoguardado normal a partir de ahí.',
    ],
  },
  {
    id: "tus-proyectos",
    category: "Abrir y crear proyectos",
    title: "Tus proyectos, guardados automáticamente",
    keywords: ["guardado", "autoguardado", "reabrir", "lista", "indexeddb", "renombrar"],
    content: [
      'Sección "Tus proyectos" en la pantalla inicial (solo visible si ya tienes alguno): cualquier proyecto abierto desde carpeta o creado con el asistente se autoguarda en este navegador — clic para reabrirlo tal cual lo dejaste, sin volver a pasar por la carpeta original.',
      "Pasa el ratón sobre un proyecto de la lista para ver el lápiz de renombrar, dentro de su misma fila.",
    ],
  },
  {
    id: "importar-copia",
    category: "Abrir y crear proyectos",
    title: "Importar una copia (.ehdz)",
    keywords: ["ehdz", "importar", "fichero", "otro ordenador", "otro navegador"],
    content: [
      'Botón "Importar copia…" en la pantalla inicial: abre un fichero .ehdz exportado antes (ver "Exportar una copia portátil (.ehdz)") y lo añade como un proyecto nuevo, guardado de verdad — útil para pasar un proyecto a otro navegador o equipo, sin la carpeta original.',
    ],
  },
  {
    id: "proyectos-con-o-sin-gis",
    category: "Abrir y crear proyectos",
    title: "Proyectos con o sin GIS",
    keywords: ["shapefile", "geometria", "opcional", "sin mapa"],
    content: [
      'El GIS (el shapefile con la forma real de las subcuencas) es opcional en toda la app, tanto al abrir un proyecto real como al crear uno nuevo: si no lo tienes, o el proyecto no trae uno enlazable, la app funciona igual, solo que en vez del mapa geográfico usa el lienzo de tarjetas (ver "Vista de mapa o de tarjetas").',
      'Puedes añadir o vincular un shapefile más adelante en cualquier momento (Editar proyecto → pestaña "Capa GIS"), y también dibujar subcuencas a mano directamente sobre el mapa sin necesidad de ningún fichero (ver "Dibujar subcuencas, puntos de desagüe y acuíferos a mano").',
    ],
  },

  // --- El mapa ---
  {
    id: "vista-mapa-tarjetas",
    category: "El mapa",
    title: "Vista de mapa o de tarjetas",
    keywords: ["lienzo", "toggle", "cambiar vista", "sin geometria"],
    content: [
      "Icono de vista (arriba a la derecha, junto al lápiz de edición): alterna entre el mapa geográfico y un lienzo esquemático con las subcuencas como tarjetas. El lienzo funciona igual aunque el proyecto no tenga ninguna geometría todavía.",
    ],
  },
  {
    id: "colores-mapa",
    category: "El mapa",
    title: "Qué significan los colores de las subcuencas",
    keywords: ["gris", "estilo", "leyenda", "problema", "nieve", "rayas", "sin uso"],
    content: [
      "Verde-azulado: normal, lista para calcularse. Gris: le falta algo (zona, desagüe, datos propios de entrada...) y por eso quedará fuera del cálculo hasta que se complete.",
      "Una subcuenca con zona de nieve se marca con una trama de rayas diagonales sobre su mismo color (no un color nuevo). Un punto de desagüe o un acuífero que no use ninguna subcuenca se pinta en amarillo claro (distinto del gris de aviso).",
      "Icono de leyenda (arriba a la izquierda del mapa) explica todos estos colores/tramas de un vistazo — se puede ocultar y reabrir sin perder la posición del mapa.",
    ],
  },
  {
    id: "capas-base-mapa",
    category: "El mapa",
    title: "Cambiar el mapa de fondo",
    keywords: [
      "satelite",
      "vista aerea",
      "terreno",
      "calles",
      "capas",
      "cuencas oficiales",
      "subcuencas oficiales",
      "miteco",
      "fondo",
    ],
    content: [
      "Icono de capas (arriba a la derecha del mapa): elige entre tres fondos — Calles, Vista aérea y Terreno.",
      "Desde el mismo selector se pueden superponer, además, dos capas de referencia del MITECO — cuencas y subcuencas oficiales de España — sin relación con los datos de tu proyecto, solo para comparar visualmente. Clic derecho sobre un polígono de cualquiera de las dos para añadirlo como subcuenca del proyecto.",
    ],
  },
  {
    id: "editar-con-clic",
    category: "El mapa",
    title: "Editar una subcuenca, punto de desagüe o acuífero con un clic",
    keywords: ["clic directo", "abrir dialogo", "sin lapiz", "tarjetas"],
    content: [
      "Fuera de cualquier modo especial (selección múltiple, resultados, dibujo), clicar un elemento en el mapa o en la vista de tarjetas abre directamente su diálogo de edición — no hace falta pasar por el lápiz ni por ningún catálogo solo para ver o cambiar sus datos.",
      "El lápiz queda reservado para selección múltiple (asignar zona/desagüe/acuífero o eliminar en bloque) y para los catálogos (Zonas, Capa GIS, Dibujar...).",
    ],
  },
  {
    id: "dibujar-mapa",
    category: "El mapa",
    title: "Dibujar subcuencas, puntos de desagüe y acuíferos a mano",
    keywords: ["dibujo", "poligono", "vertice", "punto", "clic", "finalizar"],
    content: [
      'Editar proyecto → pestaña "Dibujar", elige qué quieres crear y dibuja directamente sobre el mapa base: un clic coloca un punto de desagüe; para una subcuenca o un acuífero, varios clics van poniendo vértices y "Finalizar" cierra el contorno (mínimo 3, sin límite máximo ni restricción de forma).',
      "Al terminar la geometría se te pide un nombre (y, para una subcuenca, la superficie — calculada automáticamente a partir del dibujo, pero editable).",
    ],
  },
  {
    id: "vincular-shapefile",
    category: "El mapa",
    title: "Vincular o sustituir el shapefile de subcuencas",
    keywords: ["shp", "dbf", "prj", "capa gis", "vincular"],
    content: [
      'Editar proyecto → pestaña "Capa GIS": sube un shapefile (.shp + .dbf + .prj) y elige qué columna identifica a cada subcuenca, para cuando el proyecto no trae uno enlazable o quieres cambiarlo.',
    ],
  },

  // --- Editar el proyecto ---
  {
    id: "seleccionar-subcuencas",
    category: "Editar el proyecto",
    title: "Seleccionar subcuencas",
    keywords: ["mayus", "shift", "arrastrar", "rectangulo", "seleccion"],
    content: [
      "Con el panel de edición abierto: clic sobre una subcuenca la añade o la quita de la selección; Mayús + arrastrar selecciona todas las que toque el rectángulo (solo disponible en la vista de mapa).",
    ],
  },
  {
    id: "asignar-en-bloque",
    category: "Editar el proyecto",
    title: "Asignar zona, punto de desagüe o acuífero a varias subcuencas a la vez",
    keywords: ["bloque", "aplicar", "asignacion masiva"],
    content: [
      'Con una o más subcuencas seleccionadas, el panel de edición muestra un desplegable + botón "Aplicar" para cada cosa asignable (zona, zona de nieve, punto de desagüe, acuífero) — se aplica a toda la selección de golpe.',
    ],
  },
  {
    id: "eliminar-permanente",
    category: "Editar el proyecto",
    title: "Eliminar una subcuenca, zona, punto de desagüe o acuífero",
    keywords: ["papelera", "deshacer", "recuperar", "borrar", "permanente"],
    content: [
      "Eliminar es definitivo: se pide confirmación porque no hay deshacer. Si el elemento venía de una capa GIS, la única forma de recuperarlo es volver a importar o dibujar esa geometría — se crea como nuevo, sin la configuración que tuviera antes (zona, topología, ajustes...), así que hay que reconfigurarlo.",
      'Para subcuencas hay un atajo: en Editar proyecto → "Capa GIS", subiendo otra vez el shapefile original completo aparece "Recuperar subcuencas eliminadas" — compara las geometrías del fichero con las que ya hay en el proyecto y solo ofrece añadir las que faltan, sin duplicar las que nunca se borraron.',
    ],
  },
  {
    id: "zonas",
    category: "Editar el proyecto",
    title: "Qué es una zona",
    keywords: ["parametros", "compartir", "crear zona", "cambiar modelo"],
    content: [
      'Una zona agrupa los parámetros de un modelo (lluvia-escorrentía o de nieve) que varias subcuencas pueden compartir, en vez de calibrar cada subcuenca por separado. Se crean y editan desde Editar proyecto → pestaña "Zonas".',
      "El modelo de una zona ya creada se puede cambiar más adelante — al hacerlo, sus parámetros se reinician a los valores por defecto del modelo nuevo.",
    ],
  },
  {
    id: "editar-subcuenca",
    category: "Editar el proyecto",
    title: "Editar el nombre, superficie o ajustes de una subcuenca",
    keywords: ["nombre", "superficie", "ajuste precipitacion", "ajuste etp", "datos basicos"],
    content: [
      'Clica la subcuenca (mapa o tarjeta, ver "Editar una subcuenca, punto de desagüe o acuífero con un clic") para abrir su diálogo de edición. La pestaña "Datos básicos" tiene el nombre, la superficie y los ajustes multiplicadores de precipitación/ETP/temperatura para esa subcuenca en concreto; la pestaña "Series" tiene sus datos de entrada (ver "Pegar o ver la serie de una sola subcuenca/acuífero").',
    ],
  },
  {
    id: "pegar-serie-individual",
    category: "Editar el proyecto",
    title: "Pegar o ver la serie de una sola subcuenca/acuífero",
    keywords: [
      "excel",
      "pegar",
      "estadisticos",
      "huecos",
      "media",
      "maximo",
      "minimo",
      "gap",
      "grafica",
    ],
    content: [
      'Dentro del diálogo de edición de una subcuenca (pestaña "Series") o de un acuífero, "Pegar serie…" acepta dos columnas copiadas de una hoja de cálculo (fecha dd/mm/aaaa + valor) — alternativa más fina a subir el fichero completo cuando solo hace falta corregir o completar los datos de un elemento.',
      "Si esa serie ya existía en el proyecto, las fechas pegadas tienen que coincidir exactamente con las suyas; si no, se rechaza indicando el rango esperado, en vez de mezclar dos rangos de fechas sin que se note.",
      "Junto a la gráfica se muestran el rango de fechas, los huecos detectados en el calendario y la media/máximo/mínimo de la serie.",
    ],
  },
  {
    id: "obtener-de-aemet",
    category: "Editar el proyecto",
    title: "Obtener precipitación, temperatura y ETP de AEMET automáticamente",
    keywords: [
      "aemet",
      "estacion",
      "temperatura",
      "etp",
      "hargreaves",
      "idw",
      "metodo",
      "estacion mas cercana",
      "descarga automatica",
    ],
    content: [
      'El botón "Obtener de AEMET" (pestaña "Series" del diálogo de una subcuenca) rellena precipitación, temperatura y ETP a partir de las estaciones meteorológicas de AEMET más cercanas al centro de esa subcuenca, sin subir ni pegar ningún dato a mano — "Obtener series de AEMET" en Editar proyecto → "Series de entrada" hace lo mismo para varias subcuencas a la vez.',
      "La temperatura que se guarda es la media diaria registrada por la estación ese día (tmed) — no la máxima ni la mínima. La ETP (evapotranspiración potencial) se calcula con la fórmula de Hargreaves-Samani (FAO-56): sí usa la temperatura máxima y mínima de ese día, además de la media y la latitud de la subcuenca, pero esos dos valores no quedan guardados como una serie propia — solo se usan internamente para ese cálculo.",
      'Tres métodos para combinar las estaciones o celdas candidatas de una subcuenca: "Estación más cercana" (cada día usa la más cercana que tenga dato, y si falla pasa a la siguiente), "Promedio ponderado por distancia (IDW)" — combina todas las estaciones que tengan dato ese día, dando más peso a las más cercanas; suele comportarse mejor cuando la estación principal queda algo lejos o el terreno es complejo — o "Malla ROCIO" — usa TODAS las celdas de la rejilla de AEMET (5 km, 1951-2022) que caen dentro de la subcuenca, promediando la precipitación y tomando el mínimo/máximo de temperatura; es el método que usa por defecto la descarga automática del asistente MITECO, con todo el histórico disponible en vez de solo unos años. Se puede elegir el método manualmente, tanto por subcuenca como en la descarga en bloque.',
    ],
  },
  {
    id: "series-de-entrada-panel",
    category: "Editar el proyecto",
    title: "Subir o sustituir series de entrada en un proyecto ya abierto",
    keywords: ["csv", "vincular series", "series de entrada", "reemplazar", "cargar de nuevo"],
    content: [
      'Editar proyecto → pestaña "Series de entrada": sube o sustituye cualquiera de las 4 series (precipitación/ETP obligatorias, temperatura/bombeos opcionales) sobre el proyecto ya abierto, en cualquier momento — útil para completar una precipitación/ETP que faltaba al abrir, sin tener que recrear el proyecto.',
    ],
  },
  {
    id: "calibracion-parametros",
    category: "Editar el proyecto",
    title: "Calibrar automáticamente los parámetros de una zona",
    keywords: [
      "calibracion",
      "calibrar",
      "sce-ua",
      "nash",
      "nse",
      "optimizacion",
      "serie observada",
      "caudal observado",
    ],
    content: [
      "Para usarla, contacta con info@fluit.es.",
      "Ajusta automáticamente algunos parámetros de una zona para que la aportación simulada en un punto de desagüe se parezca lo más posible a un caudal observado real, en vez de tener que probar valores a mano — usa el mismo algoritmo (SCE-UA) que el calibrador de escritorio de EvalHid.",
      'Antes hace falta una serie observada: se abre desde el propio punto de desagüe (clic en el mapa o su tarjeta) mientras el panel de Calibración está abierto — solo entonces aparece su pestaña "Serie observada", con las mismas opciones de subir/pegar/borrar que el resto de series.',
      "En el panel: elige el punto de desagüe (solo aparecen los que ya tienen serie observada), la zona a calibrar (solo las que viertan a ese punto y tengan un modelo asignado) y qué parámetros de esa zona ajustar — cada uno con un rango físico fijo, no editable.",
      "También se fijan dos periodos de fecha independientes: uno de calibración (donde de verdad se ajustan los parámetros) y otro de validación (para comprobar el resultado fuera de la muestra usada en el ajuste, no un segundo informe del mismo periodo) — más un periodo de calentamiento opcional (duración en días/semanas/meses/años) que extiende la simulación hacia atrás sin puntuarla, para no arrancar cada evaluación con los depósitos del modelo vacíos.",
      'Al calibrar se ve un gráfico en vivo de cómo mejora la función objetivo, y al terminar, una tabla con 6 indicadores de bondad de ajuste (Nash-Sutcliffe entre ellos) antes/después — "Aplicar estos parámetros" los escribe en el proyecto solo si lo confirmas, nunca en silencio.',
      "La serie observada tiene que estar en las mismas unidades que el resultado agregado del proyecto (se indica junto al selector) — la calibración no detecta ni corrige un desajuste de unidades.",
    ],
  },

  // --- Modelos ---
  {
    id: "modelos-lluvia-escorrentia",
    category: "Modelos",
    title: "Modelos lluvia-escorrentía disponibles",
    keywords: ["temez", "sac", "hbv", "gr2m", "gr4j", "awbm"],
    content: [
      "Témez, SAC, HBV, GR2M, GR4J y AWBM. Cada zona usa uno de estos seis, con sus propios parámetros calibrables.",
    ],
  },
  {
    id: "modelos-nieve",
    category: "Modelos",
    title: "Modelos de nieve (opcionales)",
    keywords: ["nieve1", "nieve2", "fusion", "nival"],
    content: [
      "Nieve1 y Nieve2: opcionales, se asignan a una zona de nieve aparte y, si una subcuenca los tiene, se aplican antes del modelo lluvia-escorrentía (la precipitación líquida que sale del modelo de nieve es la que entra al otro modelo).",
    ],
  },
  {
    id: "parametros-defecto",
    category: "Modelos",
    title: "Parámetros por defecto de cada modelo",
    keywords: ["valores de fabrica", "plantilla", "calibrar"],
    content: [
      "Cada modelo trae unos valores por defecto razonables al crear una zona nueva, editables después uno a uno — son un punto de partida para calibrar, no un valor definitivo.",
    ],
  },

  // --- Calcular ---
  {
    id: "calcular",
    category: "Calcular",
    title: "Calcular la aportación",
    keywords: ["fechas", "boton calcular", "bombeos", "negativos en acuiferos", "modelar bombeos"],
    content: [
      'Botón "Calcular": abre un diálogo para elegir el rango de fechas (dentro de lo que cubra la precipitación cargada) y dos opciones de acuíferos ("Negativos en acuíferos" y "Modelar bombeos") antes de lanzar el cálculo.',
    ],
  },
  {
    id: "avisos-bloqueantes",
    category: "Calcular",
    title: "Qué son los avisos y los bloqueantes",
    keywords: ["campana", "notificaciones", "error", "no se puede calcular", "fallo"],
    content: [
      'Un bloqueante impide calcular del todo (p. ej. no se ha cargado la serie de precipitación) — el botón "Calcular" se desactiva hasta resolverlo. Un aviso no bloquea nada, solo informa de algo a tener en cuenta (p. ej. subcuencas sin datos propios de entrada, que se excluyen del cálculo pero no impiden calcular el resto). Se ven con el icono de la campana.',
      "Si el cálculo en sí falla (un caso límite no cubierto, un dato inconsistente), aparece ahí mismo como un aviso rojo 🔴, distinto de los demás — el resto del proyecto y del mapa siguen intactos, solo falla ese intento de cálculo.",
    ],
  },
  {
    id: "simulacion-parcial",
    category: "Calcular",
    title: "Simulación parcial: subcuencas sin datos propios",
    keywords: ["excluidas", "faltan datos", "sin datos propios"],
    content: [
      "Si a algunas subcuencas les faltan datos propios de precipitación/ETP (o de temperatura, si usan un modelo de nieve), el cálculo no se bloquea para todo el proyecto: se calculan las que sí tienen datos, y se avisa explícitamente de cuáles se quedaron fuera y por qué.",
      "Lo mismo pasa si la precipitación o la ETP de una subcuenca tienen alguna celda vacía o con un valor no numérico dentro del rango calculado (no un día entero que falta, sino un dato suelto dentro de un día que sí está presente): esa subcuenca se excluye del resultado igual que si no tuviera datos, con un aviso aparte tras calcular que indica cuál y qué fechas exactas tienen el problema.",
    ],
  },
  {
    id: "huecos-fecha-bloqueante",
    category: "Calcular",
    title: "Huecos de fecha en las series: por qué bloquean el cálculo",
    keywords: ["hueco", "fecha que falta", "dia que falta", "gap", "bloqueante", "calendario"],
    content: [
      "Si a la serie de precipitación, ETP, temperatura o bombeo le falta directamente un día del calendario (no un valor vacío dentro de un día presente, sino el día entero ausente de la tabla), el cálculo se bloquea para todo el proyecto, no solo para las subcuencas afectadas — el aviso indica las fechas exactas que faltan.",
      'La única forma de resolverlo es corregir el fichero de entrada (añadir la fila que falta) y volver a subirlo — a diferencia de una celda vacía dentro de un día presente (ver "Simulación parcial: subcuencas sin datos propios"), la app no puede rodear un hueco de fecha excluyendo solo la subcuenca afectada.',
    ],
  },
  {
    id: "recalcular",
    category: "Calcular",
    title: "Volver a calcular sin repetir el diálogo",
    keywords: ["recalcular", "icono flechas"],
    content: [
      'Una vez calculado al menos una vez, aparece un icono de recalcular junto al botón "Calcular" que repite el cálculo con las mismas opciones (fechas, acuíferos...) sin volver a preguntarlas.',
    ],
  },

  // --- Resultados ---
  {
    id: "panel-resultados",
    category: "Resultados",
    title: "El panel de resultados",
    keywords: [
      "grafica",
      "tabla",
      "aportacion anual",
      "hm3",
      "punto de desague",
      "zona",
      "unidades",
      "acuifero",
    ],
    content: [
      'El resultado principal es la "Aportación por punto de desagüe" — la suma de todas las subcuencas que viertan a cada punto, no una serie por subcuenca. Hay pestañas para verlo también por Zona (agrupado), por Subcuenca (diagnóstico, sin agregar) y por Acuífero (el balance propio de cada acuífero — recarga, bombeo y total —, sin desglose superficial/subterránea porque un acuífero no tiene componente superficial propia).',
      "Un selector en la cabecera del panel cambia las unidades (p. ej. Hm³) al vuelo, sin recalcular.",
      "Qué subcuenca/punto de desagüe ver también se puede elegir clicando directamente en el mapa, mientras el panel de Resultados esté abierto (la Zona no tiene geometría propia, así que esa solo se elige desde la lista).",
    ],
  },
  {
    id: "detalle-interno",
    category: "Resultados",
    title: 'Ver el detalle interno de una subcuenca ("resultados parciales")',
    keywords: ["ver detalle", "humedad", "series internas", "seleccionar series"],
    content: [
      'Botón "Ver detalle" en la fila de una subcuenca: muestra las series internas del modelo paso a paso (humedad del suelo, escorrentía, flujo base, contenido de nieve...) con un selector para elegir cuáles superponer en la misma gráfica.',
    ],
  },
  {
    id: "descargar-csv",
    category: "Resultados",
    title: "Descargar resultados en CSV",
    keywords: ["exportar", "csv", "descargar"],
    content: [
      'Botón "Descargar CSV" (en el panel de resultados general, y también dentro del detalle de cada subcuenca): genera un fichero de texto con los valores calculados, listo para abrir en una hoja de cálculo.',
    ],
  },

  // --- Otros ---
  {
    id: "tema-oscuro",
    category: "Otros",
    title: "Modo oscuro / claro",
    keywords: ["tema", "luna", "sol", "dark mode"],
    content: [
      "Icono de engranaje (Ajustes, arriba a la derecha del todo, y también en la pantalla inicial): dentro, un botón con icono de luna/sol cambia el tema. La preferencia se recuerda entre sesiones en este mismo navegador.",
    ],
  },
  {
    id: "idioma",
    category: "Otros",
    title: "Cambiar de idioma",
    keywords: ["language", "ingles", "español", "es", "en"],
    content: [
      "Dentro de Ajustes (icono de engranaje, arriba a la derecha, y también en la pantalla inicial): el idioma que se está viendo siempre queda resaltado, no hace falta adivinar cuál elegir.",
      "Sin nada elegido todavía, la app arranca en el idioma del navegador (inglés si el navegador está en inglés, español en cualquier otro caso) — una vez eliges uno a mano, esa elección se recuerda entre sesiones en este mismo navegador y ya no depende del navegador.",
    ],
  },
  {
    id: "persistencia",
    category: "Otros",
    title: "¿Se guarda mi trabajo?",
    keywords: [
      "guardar",
      "persistencia",
      "memoria",
      "cerrar pestaña",
      "perder cambios",
      "autoguardado",
      "indexeddb",
    ],
    content: [
      'Sí, automáticamente: cada cambio se guarda en este navegador (IndexedDB) al segundo de dejar de escribir/editar, sin ningún botón de guardar. Puedes cerrar la pestaña o recargar la página sin perder nada — al volver a abrir la app, el proyecto sigue en "Tus proyectos" tal como lo dejaste.',
      'La única excepción son los ejemplos ("Con GIS"/"Sin GIS"): mientras no hagas "Guardar copia como…", sus cambios no persisten entre sesiones (ver "Probar con un ejemplo").',
      "La carpeta original de un proyecto abierto desde disco no vuelve a tocarse tras abrirlo — puedes moverla o borrarla sin que afecte a lo ya guardado en el navegador.",
    ],
  },
  {
    id: "sincronizacion-drive",
    category: "Otros",
    title: "Sincronizar tus proyectos con Google Drive",
    keywords: [
      "drive",
      "google",
      "sincronizar",
      "conectar",
      "nube",
      "otro ordenador",
      "otro navegador",
    ],
    content: [
      'Dentro de Ajustes (icono de engranaje, arriba a la derecha): "Conectar" bajo "Google Drive" enlaza este navegador con tu cuenta de Google, una vez — no proyecto por proyecto. A partir de ahí, cada proyecto que se guarda aquí se sube también a Drive en segundo plano, sin bloquear ni esperar al guardado local: el navegador manda siempre.',
      "Al conectar (o reconectar), la app empareja lo que ya hay en Drive con tus proyectos locales: uno que solo esté en Drive se descarga; uno que solo esté en local se pregunta si subirlo; si un mismo proyecto existe en los dos sitios y difiere, se avisa de un conflicto (con la opción de usar la versión de Drive) en vez de sobrescribir sin más en ningún sentido.",
      "Útil sobre todo para tener los mismos proyectos disponibles en más de un ordenador o navegador.",
    ],
  },
  {
    id: "gestion-proyecto-abierto",
    category: "Otros",
    title: "Renombrar, guardar copia, eliminar, exportar o actualizar el proyecto abierto",
    keywords: [
      "menu",
      "tres puntos",
      "renombrar proyecto",
      "guardar copia como",
      "eliminar proyecto",
      "exportar",
      "ehdz",
      "csv",
      "actualizar desde csv",
      "cerrar proyecto",
    ],
    content: [
      'Junto al nombre del proyecto, en la barra superior: "Renombrar proyecto…", y el menú "⋮" con "Guardar copia como…" (crea un proyecto nuevo independiente, sin tocar el actual — también sirve para convertir un ejemplo en un proyecto real), "Exportar copia…", "Actualizar desde CSVs…" y "Eliminar este proyecto" (permanente, deshabilitado mientras el proyecto sea un ejemplo sin copia propia).',
      '"Exportar copia…" pregunta entre dos formatos: una copia portátil .ehdz (solo esta app puede volver a abrirla; pensada para mover el proyecto entre navegadores o equipos) o un conjunto de ficheros CSV en un .zip (cuencas, zonas, parámetros, acuíferos...) para revisarlos o usarlos con otras herramientas, con un informe aparte de cualquier dato incompleto detectado al exportar.',
      '"Actualizar desde CSVs…" hace el camino contrario: aplica sobre el proyecto ya abierto una carpeta con esos mismos CSV editados a mano (por ejemplo, tras corregir parámetros fuera de la app) — empareja por nombre, y antes de aplicar nada muestra cuántas subcuencas/zonas/acuíferos/puntos de desagüe se actualizarían o crearían.',
      '"Cerrar proyecto" (junto al nombre) guarda inmediatamente y vuelve a la pantalla inicial.',
    ],
  },
];
